import { useState, useRef, useCallback } from 'react';
import logger from '@/utils/logger';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Upload, X, Loader2, Image as ImageIcon, AlertCircle, Plus, GripVertical, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_IMAGES = 5;

export default function MultiImageUpload({ 
  onImagesChange, 
  currentImages = [],
  className = '',
}) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  
  const [images, setImages] = useState(currentImages);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return isEnglish 
        ? 'Only images (JPEG, PNG, GIF, WebP) are allowed' 
        : 'Vain kuvat (JPEG, PNG, GIF, WebP) ovat sallittuja';
    }
    if (file.size > MAX_FILE_SIZE) {
      return isEnglish 
        ? 'File is too large. Maximum size is 5MB' 
        : 'Tiedosto on liian suuri. Maksimikoko on 5MB';
    }
    return null;
  };

  const uploadFile = async (file) => {
    const validationError = validateFile(file);
    if (validationError) {
      toast.error(validationError);
      return null;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const { url } = response.data;
      return `${BACKEND_URL}${url}`;
    } catch (err) {
      logger.error('Upload failed:', err);
      const errorMsg = err.response?.data?.detail || (isEnglish ? 'Upload failed' : 'Lataus epäonnistui');
      toast.error(errorMsg);
      return null;
    }
  };

  const handleFilesSelect = async (files) => {
    if (images.length >= MAX_IMAGES) {
      toast.error(isEnglish 
        ? `Maximum ${MAX_IMAGES} images allowed` 
        : `Maksimissaan ${MAX_IMAGES} kuvaa sallittu`);
      return;
    }

    const filesToUpload = Array.from(files).slice(0, MAX_IMAGES - images.length);
    setUploading(true);

    const uploadedUrls = [];
    for (const file of filesToUpload) {
      const url = await uploadFile(file);
      if (url) uploadedUrls.push(url);
    }

    if (uploadedUrls.length > 0) {
      const newImages = [...images, ...uploadedUrls];
      setImages(newImages);
      onImagesChange?.(newImages);
      toast.success(isEnglish 
        ? `${uploadedUrls.length} image(s) uploaded` 
        : `${uploadedUrls.length} kuva(a) ladattu`);
    }

    setUploading(false);
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files?.length) handleFilesSelect(files);
    e.target.value = ''; // Reset input
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer.files;
    if (files?.length) handleFilesSelect(files);
  }, [images]);

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    onImagesChange?.(newImages);
  };

  const moveImage = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= images.length) return;
    const newImages = [...images];
    const [removed] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, removed);
    setImages(newImages);
    onImagesChange?.(newImages);
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div className={`w-full ${className}`}>
      <label className="block text-sm font-medium text-stone-700 mb-2">
        {isEnglish ? 'Photos' : 'Kuvat'} ({images.length}/{MAX_IMAGES})
      </label>
      
      {/* Images Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-4">
          {images.map((url, index) => (
            <div 
              key={`${url}-${index}`}
              className="relative aspect-square rounded-xl overflow-hidden bg-stone-100 group"
            >
              <img
                src={url}
                alt={`${isEnglish ? 'Photo' : 'Kuva'} ${index + 1}`}
                className="w-full h-full object-cover cursor-pointer"
                onClick={() => openLightbox(index)}
              />
              {/* Overlay controls */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {/* Move buttons */}
                <button
                  type="button"
                  onClick={() => moveImage(index, index - 1)}
                  disabled={index === 0}
                  className="p-1.5 rounded-full bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label={isEnglish ? 'Move left' : 'Siirrä vasemmalle'}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => moveImage(index, index + 1)}
                  disabled={index === images.length - 1}
                  className="p-1.5 rounded-full bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label={isEnglish ? 'Move right' : 'Siirrä oikealle'}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                {/* Remove button */}
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="p-1.5 rounded-full bg-red-500/80 text-white hover:bg-red-500"
                  aria-label={isEnglish ? 'Remove' : 'Poista'}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              {/* Primary badge */}
              {index === 0 && (
                <span className="absolute top-1 left-1 px-2 py-0.5 rounded-full bg-primary text-white text-xs font-medium">
                  {isEnglish ? 'Main' : 'Pää'}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload Zone */}
      {images.length < MAX_IMAGES && (
        <div
          className={`relative rounded-2xl border-2 border-dashed p-6 transition-all cursor-pointer
            ${dragActive ? 'border-primary bg-primary/5' : 'border-stone-200 hover:border-stone-300'}
          `}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="flex flex-col items-center justify-center text-center">
            {uploading ? (
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
            ) : (
              <div className="h-10 w-10 rounded-full bg-stone-100 flex items-center justify-center mb-2">
                <Plus className="h-5 w-5 text-stone-400" />
              </div>
            )}
            
            <p className="text-sm text-muted-foreground">
              {uploading 
                ? (isEnglish ? 'Uploading...' : 'Ladataan...')
                : (isEnglish 
                    ? 'Drag photos here or click to browse' 
                    : 'Vedä kuvat tähän tai napsauta selataksesi')
              }
            </p>
            
            {!uploading && (
              <p className="text-xs text-muted-foreground mt-1">
                {isEnglish 
                  ? `Up to ${MAX_IMAGES - images.length} more • JPEG, PNG, GIF, WebP • Max 5MB each`
                  : `Enintään ${MAX_IMAGES - images.length} lisää • JPEG, PNG, GIF, WebP • Max 5MB/kuva`}
              </p>
            )}
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_TYPES.join(',')}
        onChange={handleFileInputChange}
        multiple
        className="hidden"
        aria-label={isEnglish ? 'Upload images' : 'Lataa kuvia'}
      />

      {/* Lightbox */}
      {lightboxOpen && images.length > 0 && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
          >
            <X className="h-6 w-6" />
          </button>

          {images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
                }}
                className="absolute left-4 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white"
              >
                <ChevronLeft className="h-8 w-8" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
                }}
                className="absolute right-4 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white"
              >
                <ChevronRight className="h-8 w-8" />
              </button>
            </>
          )}

          <div onClick={(e) => e.stopPropagation()} className="max-w-4xl max-h-[85vh] px-4">
            <img
              src={images[lightboxIndex]}
              alt={`${isEnglish ? 'Photo' : 'Kuva'} ${lightboxIndex + 1}`}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            <p className="text-center text-white/70 text-sm mt-4">
              {lightboxIndex + 1} / {images.length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
